/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-65 — turning a rendered block of text sideways
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHY THE BITMAP AND NOT THE CANVAS. A `ctx.rotate()` before drawing would
 * re-rasterise every glyph at 90°, where hinting and antialiasing land
 * differently — and at ONE BIT that is a different sticker, not the same one
 * turned round. Rotating the finished 1-bit bitmap moves dots that already
 * exist, so what the florist approved in the preview is exactly what burns.
 *
 * It also makes the transform pure: no DOM, no canvas, testable in jest like
 * `packCatRow` and `wrapText` already are. A transposition that comes out
 * mirrored is invisible in code and obvious on paper, which is precisely the
 * kind of mistake this repo pays tests to catch.
 *
 * THE GEOMETRY, because it is easy to get backwards. A roll printer has a fixed
 * line of dots ACROSS the paper (384 on a 58 mm roll) and an unbounded feed
 * direction. So one text axis is always bounded and the other is free:
 *
 *   HORIZONTAL — lines wrap at 384 dots (~18-22 characters); the number of
 *     lines grows the paper. At 58 mm a gift message becomes a ransom note of
 *     three-word lines.
 *   VERTICAL — the number of LINES is bounded (~13 at 22 px); each line runs
 *     as long as it likes and grows the paper. One sentence per line, a narrow
 *     strip. This is why vertical is the default, and the reason is written
 *     down so nobody later "fixes" it.
 */

export interface MonoBitmap {
  width: number;
  height: number;
  /** One byte per pixel: 1 = burn, 0 = leave white. */
  pixels: Uint8Array;
}

/**
 * Quarter turn.
 *
 * `cw` sends the bitmap's TOP edge to the right-hand side of the result; `ccw`
 * sends it to the left. Which one a florist wants depends on which end of the
 * strip they treat as the top as it leaves the printer, and that cannot be
 * settled from a desk — it is decided on the first real sticker. Keeping both
 * makes flipping it a one-word change rather than a rewrite.
 */
export function rotateBitmap90(
  bitmap: MonoBitmap,
  direction: "cw" | "ccw",
): MonoBitmap {
  const { width: w, height: h, pixels } = bitmap;
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const value = pixels[y * w + x];
      if (!value) continue;
      // Result is h wide and w tall.
      const [nx, ny] =
        direction === "cw" ? [h - 1 - y, x] : [y, w - 1 - x];
      out[ny * h + nx] = value;
    }
  }
  return { width: h, height: w, pixels: out };
}

/**
 * Widens a bitmap to the paper, leaving the extra as blank paper.
 *
 * `packCatRow` already left-pads a narrow row for free, so this exists for the
 * CENTRED case: a short vertical note is a narrow strip, and a strip centred on
 * the roll peels straight instead of hugging one edge. Never narrows — losing
 * dots must be a decision made where the text is laid out, with a warning, not
 * a silent trim down here.
 */
export function padBitmapTo(
  bitmap: MonoBitmap,
  width: number,
  align: "left" | "center" = "center",
): MonoBitmap {
  if (width <= bitmap.width) return bitmap;
  const offset =
    align === "center" ? Math.floor((width - bitmap.width) / 2) : 0;
  const out = new Uint8Array(width * bitmap.height);
  for (let y = 0; y < bitmap.height; y += 1) {
    for (let x = 0; x < bitmap.width; x += 1) {
      const value = bitmap.pixels[y * bitmap.width + x];
      if (value) out[y * width + x + offset] = value;
    }
  }
  return { width, height: bitmap.height, pixels: out };
}
